// src/components/ValidationDiffTable.tsx
import React, { useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, GridApi, GridOptions } from "ag-grid-enterprise";
import { FaCheckCircle, FaTimesCircle, FaPlusCircle } from "react-icons/fa";

import { CompCodeName } from "./CompCodeName";
import { RoomValidationDiffItem } from "@backend/src/shared/schemas/room/roomValidationSchema";

interface ValidationDiffTableProps {
  diffItems: RoomValidationDiffItem[];
  standardCountHeaderName: string;
}

export const ValidationDiffTable: React.FC<ValidationDiffTableProps> = ({
  diffItems,
  standardCountHeaderName,
}) => {
  const gridRef = useRef<AgGridReact<RoomValidationDiffItem>>(null);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [query, setQuery] = useState("");

  const defaultColDef = useMemo<ColDef<RoomValidationDiffItem>>(
    () => ({
      sortable: false,
      filter: false,
      resizable: false,
      suppressMovable: true,
      suppressHeaderMenuButton: true, // ✅ tar bort tre-prickar/kolumnmeny
    }),
    []
  );

  const columnDefs = useMemo<ColDef<RoomValidationDiffItem>[]>(
    () => [
      {
        headerName: "",
        width: 44,
        cellRenderer: (params: any) => {
          const delta = params.data?.delta;
          if (delta === 0) return <FaCheckCircle color="#2f9e44" size={18} title="Match" />;
          if (delta < 0) return <FaTimesCircle color="#e03131" size={18} title="Missing" />;
          if (delta > 0) return <FaPlusCircle color="#1971c2" size={18} title="Extra" />;
          return null;
        },
      },
      {
        headerName: "",
        field: "label",
        hide: true,
      },
      {
        headerName: "CompCode",
        field: "compCode",
        flex: 1,
        minWidth: 140,
        cellRenderer: (params: any) => {
          const compCode = params.value as string | undefined;
          const label = params.data?.label as string | undefined;
          return <CompCodeName compCode={compCode} name={label} tooltipFallback="No type name" />;
        },
        // ✅ Gör så quick filter även matchar på label fast label-kolumnen är hidden
        getQuickFilterText: (params) => {
          const compCode = String(params.data?.compCode ?? "");
          const label = String(params.data?.label ?? "");
          return `${compCode} ${label}`.trim();
        },
      },
      {
        headerName: standardCountHeaderName,
        field: "standardCount",
        width: 120,
        type: "rightAligned",
        cellStyle: { fontWeight: 500 },
      },
      {
        headerName: "Antall i modellen",
        field: "instanceCount",
        width: 130,
        type: "rightAligned",
      },
      {
        headerName: "Δ",
        field: "delta",
        width: 70,
        type: "rightAligned",
        cellStyle: (p) => ({
          color: p.value > 0 ? "#1971c2" : p.value < 0 ? "#e03131" : "#2f9e44",
          fontWeight: 600,
        }),
      },
    ],
    [standardCountHeaderName]
  );

  const gridOptions = useMemo<GridOptions>(
    () => ({
      suppressContextMenu: true,
      allowContextMenuWithControlKey: false,
      suppressMovableColumns: true,
      headerHeight: 32,
      rowHeight: 34,
      suppressCellFocus: true,
      suppressRowClickSelection: true,
    }),
    []
  );

  const handleSearchChange = (value: string) => {
    setQuery(value);
    gridApi?.setGridOption("quickFilterText", value);
  };

  return (
    <div
      style={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <input
        value={query}
        onChange={(e) => handleSearchChange(e.currentTarget.value)}
        placeholder="Søk…"
        style={{
          width: "100%",
          height: 32,
          padding: "0 10px",
          borderRadius: 6,
          border: "1px solid rgba(0,0,0,0.15)",
          outline: "none",
          boxSizing: "border-box",
        }}
      />

      {/* Viktigt: flex:1 + minHeight:0 => grid får scrollen och fyller resterande höjd */}
      <div className="ag-theme-quartz" style={{ flex: 1, minHeight: 0, width: "100%" }}>
        <AgGridReact<RoomValidationDiffItem>
          ref={gridRef}
          rowData={diffItems}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          gridOptions={gridOptions}
          sideBar={false}
          animateRows={false}
          onGridReady={(params) => {
            setGridApi(params.api);
            // Om man redan har text i input vid mount
            if (query) params.api.setGridOption("quickFilterText", query);
          }}
        />
      </div>
    </div>
  );
};
